import React from "react";
import { Flex, LoadingIndicator, Text, Button } from "@jsq/ds/core";
import { Sizes, Colors } from "@jsq/ds/styles";
import * as styles from "./styles_vanilla.css";

interface LoadingOverlayProps {
  message?: string;
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  onCancel?: () => void;
  cancelText?: string;
  children: React.ReactNode;
  loadingSize?: "small" | "medium" | "large";
  show?: boolean;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  message = "Loading...",
  loading = false,
  error = null,
  onRetry,
  onCancel,
  cancelText = "Back",
  children,
  loadingSize = "large",
  show, // For backward compatibility
}) => {
  // Support both new props and legacy 'show' prop
  const isLoading = loading || show;
  const hasError = !!error;
  const shouldShowOverlay = isLoading || hasError;

  if (!shouldShowOverlay) {
    return <>{children}</>;
  }

  // Helper function to detect permission errors
  const isPermissionError = (errorMessage: string) => {
    const lowerMessage = errorMessage.toLowerCase();
    return ["permission", "access", "unauthorized", "forbidden", "not allowed", "denied"].some(
      (keyword) => lowerMessage.includes(keyword),
    );
  };

  const renderOverlayContent = () => {
    if (isLoading) {
      // Loading state
      return (
        <>
          <LoadingIndicator size={loadingSize} data-testid="loading-indicator" />
          <Text variant="regular-md" color={Colors.Neutral600}>
            {message}
          </Text>
        </>
      );
    }

    if (hasError) {
      // Error state
      const errorMessage = error.message;
      const isPermError = isPermissionError(errorMessage);

      return (
        <>
          <Text variant="regular-lg" color={Colors.Danger600} style={{ fontWeight: "600" }}>
            {isPermError ? "Access Denied" : "Failed to Load"}
          </Text>
          <Text variant="regular-md" color={Colors.Neutral600} className={styles.errorText}>
            {isPermError
              ? "You don't have permission to access this resource. Please contact your administrator."
              : `Unable to load: ${errorMessage}`}
          </Text>
          <Flex gap={Sizes.MD}>
            {onRetry && (
              <Button variant="primary" onClick={onRetry}>
                Retry
              </Button>
            )}
            {onCancel && (
              <Button variant="neutral" onClick={onCancel}>
                {cancelText}
              </Button>
            )}
          </Flex>
        </>
      );
    }

    return null;
  };

  return (
    <Flex className={styles.container}>
      {/* Content underneath */}
      <Flex className={shouldShowOverlay ? styles.contentDimmed : styles.contentNormal}>
        {children}
      </Flex>

      {/* Overlay on top */}
      {shouldShowOverlay && (
        <Flex
          alignItems="center"
          justifyContent="center"
          className={hasError ? styles.errorOverlay : styles.overlay}
          gap={Sizes.MD}
        >
          {renderOverlayContent()}
        </Flex>
      )}
    </Flex>
  );
};

export default LoadingOverlay;
